import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render/runtime errors thrown by the MapLibre map subtree (e.g. a
 * lost WebGL context or a GL failure on a constrained browser) and shows a
 * recoverable fallback instead of white-screening the entire application.
 */
class MapErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[MapErrorBoundary] map crashed:', error, info?.componentStack);
  }

  handleReload = () => {
    // A fresh reload re-creates the WebGL context from scratch.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            padding: '1.5rem',
            textAlign: 'center',
            background: '#f8fafc',
            color: '#334155'
          }}
        >
          <div style={{ fontSize: '2rem' }}>🗺️</div>
          <div style={{ fontWeight: 600 }}>The map couldn't be displayed</div>
          <div style={{ fontSize: '0.875rem', maxWidth: '22rem' }}>
            This can happen on some in-car browsers when the map runs low on
            graphics memory. Reloading usually fixes it.
          </div>
          <button
            onClick={this.handleReload}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: '#2563eb',
              color: 'white',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Reload map
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default MapErrorBoundary;
